import { ChevronDown } from "lucide-react";

/**
 * DISCLOSURE — a thin wrapper over the NATIVE <details>/<summary>.
 *
 * LOCAL TO /pipeline FOR NOW, AND DELIBERATELY SO. The design contract lists Disclosure as a
 * shared primitive, but src/components/primitives.tsx is owned by a concurrent workstream and
 * I am scoped out of it. Implemented here, flagged for consolidation. When it moves, it moves
 * verbatim — the API below is already the contract's ({id, title, meta, children, open,
 * onToggle, locked, testId}) plus the three additive props described below.
 *
 * THREE THINGS THAT ARE LOAD-BEARING AND MUST SURVIVE THE MOVE:
 *
 * 1. NO JS HEIGHT ANIMATION. Animating the body's height desynchronizes the visual state from
 *    the native `open` attribute — which is exactly the attribute the smoke harness reads to
 *    assert that an undecided edit cannot be hidden. [open] is the styling hook; the only
 *    thing that moves is the chevron.
 *
 * 2. THE SUMMARY IS THE TAP TARGET, full-row, at >= --tap. `summary` is already in the
 *    harness's TAP_SELECTOR *and* its CLIP_SELECTOR, so it must be >= 44px tall and must
 *    never clip its content.
 *
 * 3. `locked` (force-open, non-collapsible) is in the API FROM DAY ONE so /review can adopt
 *    it later — but /review is NOT rewired in this change.
 *
 * WHAT THIS ROUND ADDS, AND WHY EACH IS STRUCTURE RATHER THAN DECORATION:
 *
 *   `ordinal`  — a mono 01..06 in its own summary track. It gives the six checks a numbered
 *                spine (this is a SPECIFICATION, and a spec is ordered), and it gives all six
 *                titles ONE hard left edge, by construction rather than by eye.
 *
 *   `sentence` — the gate's one-line definition, rendered INSIDE the summary and therefore
 *                visible with zero clicks. It used to live behind the chevron, which meant the
 *                reference content of a reference page was hidden by default and the desktop
 *                column it sat in was a 600px void in its resting state. Two-line clamp when
 *                closed (CSS only), full text when open.
 *
 *   `metaVariant` — "count" renders the meta as an accent chip; "neutral" renders it as a grey
 *                chip; "text" keeps the plain caption line. Reviewer sign-off's meta is
 *                "Human verdict — no validator", which is NOT a count, so it takes the neutral
 *                chip: the one gate a machine cannot clear looks like a different species
 *                without borrowing a single pass/fail colour.
 *
 * `open` is CONTROLLED by the page, not held here. The page owns it because the URL hash can
 * open a gate (/pipeline#rendering) at any time, including while the page is already mounted —
 * an internal defaultOpen state would be initialised once and then ignore a later hash change.
 */
export function Disclosure({
  id,
  ordinal,
  title,
  sentence,
  meta,
  metaVariant = "text",
  codes,
  children,
  open,
  onToggle,
  locked = false,
  className,
  testId,
  summaryTestId,
  deepLinked = false
}: {
  id?: string;
  /** Mono ordinal, e.g. "01". Renders in its own leading track when present. */
  ordinal?: string;
  title: string;
  /** The always-visible definition line. Clamped to two lines while closed, via CSS. */
  sentence?: string;
  /** The count/caption line. Sits under the copy on phone, in its own track at >= 768. */
  meta?: string;
  metaVariant?: "text" | "count" | "neutral";
  /**
   * The validator ids this check runs, as a chip strip in the summary. It is the spec's
   * fingerprint: at >= 768 an operator can read WHICH codes a check runs without opening it,
   * and open it only to learn what each one does. HIDDEN ON THE PHONE by CSS — ten chips is
   * three wrapped rows on a 390px screen, and a phone reading of a spec wants the sentence, not
   * the codes. Complexity ADDED upward, which is the only direction this codebase allows.
   *
   * aria-hidden: the ids are announced in full, with their descriptions, in the table inside.
   * Reading them twice would make a screen reader's pass through the six checks longer, not
   * more informative.
   */
  codes?: string[];
  children: React.ReactNode;
  open: boolean;
  onToggle: (open: boolean) => void;
  /** Force-open and non-collapsible. Nothing on /pipeline uses it; /review will. */
  locked?: boolean;
  className?: string;
  testId?: string;
  summaryTestId?: string;
  /** The gate the URL hash named. Takes a static accent tint — no animation, no focus steal. */
  deepLinked?: boolean;
}) {
  const isOpen = locked || open;

  return (
    <details
      id={id}
      className={`disclosure ${className || ""}`}
      data-testid={testId}
      data-locked={locked ? "true" : "false"}
      data-deeplinked={deepLinked ? "true" : "false"}
      open={isOpen}
      // The native toggle event is the source of truth. We mirror it into React state rather
      // than fighting it, so the DOM's `open` attribute and the rendered state never diverge.
      onToggle={(event) => {
        const next = (event.currentTarget as HTMLDetailsElement).open;
        if (next !== isOpen) onToggle(next);
      }}
    >
      <summary
        className="disclosure-summary"
        data-testid={summaryTestId}
        onClick={(event) => {
          // A locked row cannot be collapsed. Suppressing the default here (rather than
          // early-returning from onToggle) is what actually holds it open: `open` is a
          // controlled prop, so if we let the native toggle fire and then decline to update
          // state, React never re-renders and the native <details> sticks in the wrong state.
          if (locked) event.preventDefault();
        }}
      >
        {ordinal ? (
          <span className="disclosure-ordinal" aria-hidden="true">
            {ordinal}
          </span>
        ) : null}
        <span className="disclosure-copy">
          <span className="disclosure-title">{title}</span>
          {sentence ? <span className="disclosure-sentence">{sentence}</span> : null}
          {meta ? (
            <span className="disclosure-meta" data-variant={metaVariant}>
              {meta}
            </span>
          ) : null}
          {codes?.length ? (
            <span className="disclosure-codes" aria-hidden="true">
              {codes.map((code) => (
                <span className="code-chip" key={code}>
                  {code}
                </span>
              ))}
            </span>
          ) : null}
        </span>
        {locked ? null : (
          <span className="disclosure-chevron" aria-hidden="true">
            <ChevronDown />
          </span>
        )}
      </summary>
      <div className="disclosure-body">{children}</div>
    </details>
  );
}
